<script setup>
/* global axios */
// [AstraChat] Pop-up do compositor para ENVIAR um WhatsApp Flow (Meta) direto na
// conversa, sem template — mensagem interativa (type: flow) dentro da janela de 24h.
// Lista os flows PUBLICADOS da caixa (aba Flows) e posta em
// /kanban/whatsapp_flows/send_message. O backend cria a bolha e o SendOnWhatsappService
// entrega (barra fora da janela e grava o wamid em source_id). Só caixa Cloud oficial
// (gate no ReplyBottomPanel). A resposta do cliente volta pela recepção já existente.
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import KanbanSelect from 'dashboard/routes/dashboard/kanban/KanbanSelect.vue';

const emit = defineEmits(['close']);

const { t } = useI18n();
const route = useRoute();
const currentChat = useMapGetter('getSelectedChat');

const base = computed(
  () => `/api/v1/accounts/${route.params.accountId}/kanban/whatsapp_flows`
);
const inboxId = computed(() => currentChat.value?.inbox_id);
const canReply = computed(() => currentChat.value?.can_reply !== false);

const flows = ref([]);
const loading = ref(false);
const sending = ref(false);
const error = ref('');

const flowId = ref('');
const body = ref('');
const cta = ref('');

// [AstraChat] Lembra o último flow/texto/botão usado por conta para não ter que
// preencher tudo de novo a cada envio (feedback "teria que ficar preenchendo isso
// sempre?"). Só conveniência local do navegador — guardado em try/catch porque
// localStorage pode lançar/voltar vazio (aba anônima, storage bloqueado).
const draftKey = computed(
  () => `astrachat:waflow:send:${route.params.accountId}`
);
const loadDraft = () => {
  try {
    const raw = localStorage.getItem(draftKey.value);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};
const saveDraft = () => {
  try {
    localStorage.setItem(
      draftKey.value,
      JSON.stringify({
        flowId: flowId.value,
        body: body.value,
        cta: cta.value,
      })
    );
  } catch (e) {
    // storage indisponível: segue sem lembrar, sem quebrar o envio.
  }
};

const load = async () => {
  if (!inboxId.value) return;
  loading.value = true;
  try {
    const { data } = await axios.get(base.value, {
      params: { inbox_id: inboxId.value },
    });
    flows.value = (data.data || []).filter(
      f => String(f.status).toUpperCase() === 'PUBLISHED'
    );
    // Pré-preenche com o último uso (flow só se ainda estiver publicado).
    const draft = loadDraft();
    if (draft) {
      if (
        draft.flowId &&
        flows.value.some(f => String(f.id) === String(draft.flowId))
      ) {
        flowId.value = draft.flowId;
      }
      if (!body.value && draft.body) body.value = draft.body;
      if (!cta.value && draft.cta) cta.value = draft.cta;
    }
  } catch (e) {
    error.value = e.response?.data?.error || t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.LOAD_ERROR');
  } finally {
    loading.value = false;
  }
};

const options = computed(() =>
  flows.value.map(f => ({ value: f.id, label: `${f.name} (${f.id})` }))
);
const flowPlaceholder = computed(() =>
  flows.value.length
    ? t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.FLOW_PH')
    : t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.NO_FLOWS')
);

const canSend = computed(
  () => canReply.value && flowId.value && body.value.trim() && !sending.value
);

const send = async () => {
  if (!canSend.value) return;
  sending.value = true;
  error.value = '';
  try {
    await axios.post(`${base.value}/send_message`, {
      conversation_id: currentChat.value?.id,
      flow_id: flowId.value,
      body: body.value.trim(),
      flow_cta: cta.value.trim(),
    });
    saveDraft();
    useAlert(t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.SENT'));
    emit('close');
  } catch (e) {
    error.value = e.response?.data?.error || t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.SEND_ERROR');
  } finally {
    sending.value = false;
  }
};

onMounted(load);
</script>

<template>
  <teleport to="body">
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      @click.self="emit('close')"
    >
      <div
        class="w-full max-w-md rounded-xl bg-n-solid-1 border border-n-weak shadow-xl flex flex-col max-h-[85vh]"
      >
        <div class="flex items-center gap-2 px-4 py-3 border-b border-n-weak">
          <h3 class="text-heading-3 text-n-slate-12 flex-1">
            {{ t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.TITLE') }}
          </h3>
          <button
            type="button"
            class="grid place-items-center size-7 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="p-4 overflow-y-auto flex flex-col gap-3">
          <p
            v-if="error"
            class="text-sm text-n-ruby-11 bg-n-ruby-2 rounded-lg px-3 py-2"
          >
            {{ error }}
          </p>
          <p
            v-if="!canReply"
            class="text-sm text-n-amber-11 bg-n-amber-2 rounded-lg px-3 py-2"
          >
            {{ t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.OUT_OF_WINDOW') }}
          </p>

          <label class="text-sm text-n-slate-12 flex flex-col gap-1">
            {{ t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.FLOW_LABEL') }}
            <KanbanSelect
              v-model="flowId"
              :options="options"
              :placeholder="flowPlaceholder"
              inline
            />
          </label>

          <label class="text-sm text-n-slate-12 flex flex-col gap-1">
            {{ t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.BODY_LABEL') }}
            <textarea
              v-model="body"
              rows="3"
              :placeholder="t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.BODY_PH')"
              class="rounded-lg border border-n-weak bg-n-alpha-1 px-2 py-1.5 text-sm text-n-slate-12"
            />
          </label>

          <label class="text-sm text-n-slate-12 flex flex-col gap-1">
            {{ t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.CTA_LABEL') }}
            <input
              v-model="cta"
              maxlength="30"
              :placeholder="t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.CTA_PH')"
              class="h-9 rounded-lg border border-n-weak bg-n-alpha-1 px-2 text-sm text-n-slate-12"
            >
          </label>

          <p v-if="loading" class="text-xs text-n-slate-10">
            {{ t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.LOADING') }}
          </p>
        </div>

        <div class="flex justify-end gap-2 px-4 py-3 border-t border-n-weak">
          <NextButton
            :label="t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.CANCEL')"
            slate
            faded
            sm
            @click="emit('close')"
          />
          <NextButton
            :label="t('INBOX_MGMT.WHATSAPP_FLOWS.SEND.SEND')"
            blue
            sm
            :disabled="!canSend"
            @click="send"
          />
        </div>
      </div>
    </div>
  </teleport>
</template>
